import { forwardRef } from "react";
import { Loader2, Database, BookOpen, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { FactPopover } from "@/components/programs/FactPopover";
import { FactText } from "@/components/facts/FactText";
import { Button } from "@/components/ui/button";
import type { ChatMessage } from "@/hooks/useChatHistory";

interface Props {
  messages: ChatMessage[];
  loading: boolean;
  compact?: boolean;
  onExpandRequest?: () => void;
  onActionClick?: (actionKey: string) => void;
}

const TRUNCATE_LENGTH = 300;

export const ChatMessages = forwardRef<HTMLDivElement, Props>(
  ({ messages, loading, compact = false, onExpandRequest, onActionClick }, ref) => {
    return (
      <div ref={ref} className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg, idx) => {
          const content = msg.content;
          const isTruncated = compact && content.length > TRUNCATE_LENGTH;
          const displayContent = isTruncated
            ? content.slice(0, TRUNCATE_LENGTH) + "…"
            : content;

          // Only show actions on the last assistant message
          const isLastAssistant =
            msg.role === "assistant" &&
            idx === messages.length - 1 &&
            msg.actions &&
            msg.actions.length > 0;

          return (
            <div
              key={msg.id}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`rounded-lg px-3 py-2 text-sm ${
                  compact ? "max-w-full" : "max-w-[80%]"
                } ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary border border-border text-foreground/90 font-ai"
                }`}
              >
                <div className="whitespace-pre-wrap leading-relaxed text-xs">
                  <FactText text={displayContent} />
                </div>

                {isTruncated && onExpandRequest && (
                  <button
                    onClick={onExpandRequest}
                    className="text-[10px] text-primary mt-1 hover:underline"
                  >
                    Ouvrir en grand pour le détail ↗
                  </button>
                )}

                {/* Action buttons for guided flow */}
                {isLastAssistant && !loading && (
                  <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-border/50">
                    {msg.actions!.map((action) => (
                      <Button
                        key={action.key}
                        variant="outline"
                        size="sm"
                        className="text-[10px] h-7 px-2 gap-1"
                        onClick={() => onActionClick?.(action.key)}
                      >
                        <span>{action.icon}</span>
                        <span>{action.label}</span>
                      </Button>
                    ))}
                  </div>
                )}

                {/* Metadata — only in non-compact */}
                {!compact && msg.role === "assistant" && msg.metadata && msg.content && (
                  <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-border/50">
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Database className="h-3 w-3" />
                      <span>{msg.metadata.total_facts_in_db} faits</span>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <BookOpen className="h-3 w-3" />
                      <span>{msg.metadata.eligible_after_level_filter} éligibles</span>
                    </div>
                    {msg.metadata.profile_loaded && (
                      <div className="flex items-center gap-1 text-[10px] text-primary">
                        <User className="h-3 w-3" />
                        <span>Profil</span>
                      </div>
                    )}
                    {msg.metadata.fact_ids && msg.metadata.fact_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 ml-auto">
                        {msg.metadata.fact_ids.slice(0, 6).map((f, fi) => (
                          <FactPopover key={f.id} factId={f.id} index={fi}>
                            <button className="inline-flex items-center justify-center px-1.5 py-0 text-[8px] font-bold text-primary bg-primary/10 rounded-full cursor-pointer hover:bg-primary/20 transition-colors border border-primary/20 hover:border-primary/40">
                              {fi + 1}
                            </button>
                          </FactPopover>
                        ))}
                        {msg.metadata.fact_ids.length > 6 && (
                          <span className="text-[8px] text-muted-foreground">
                            +{msg.metadata.fact_ids.length - 6}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {loading && messages[messages.length - 1]?.role === "assistant" && !messages[messages.length - 1]?.content && (
          <div className="flex justify-start">
            <div className="rounded-lg bg-secondary border border-border px-3 py-2 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">Analyse en cours…</span>
            </div>
          </div>
        )}
      </div>
    );
  }
);

ChatMessages.displayName = "ChatMessages";
